import {
  queryPermission,
  requestPermission,
  type PermissionState,
} from "./system-permissions";
import type {
  OnboardingPermissionId,
  OnboardingPermissionStatus,
} from "./onboarding-v2";

interface PermissionRequestInput {
  apiBase: string;
  getAccessToken: () => Promise<string | null>;
  permission: OnboardingPermissionId;
}

const BUILD_VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY || "";
let runtimeVapidPublicKey: string | null = null;

export async function requestOnboardingPermission({
  apiBase,
  getAccessToken,
  permission,
}: PermissionRequestInput): Promise<OnboardingPermissionStatus> {
  if (permission === "microphone")
    return toOnboardingStatus(await requestPermission("microphone"));
  if (permission === "camera")
    return toOnboardingStatus(await requestPermission("camera"));
  return requestPushPermission(apiBase, getAccessToken);
}

export async function queryOnboardingPermission(
  permission: OnboardingPermissionId,
): Promise<OnboardingPermissionStatus> {
  if (permission === "microphone")
    return toOnboardingStatus(await queryPermission("microphone"));
  if (permission === "camera")
    return toOnboardingStatus(await queryPermission("camera"));
  if (typeof window === "undefined" || !("Notification" in window))
    return "unknown";
  return Notification.permission === "default"
    ? "unknown"
    : Notification.permission;
}

async function requestPushPermission(
  apiBase: string,
  getAccessToken: () => Promise<string | null>,
): Promise<OnboardingPermissionStatus> {
  if (typeof window === "undefined" || !("Notification" in window))
    return "unknown";
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
    const permission = await Notification.requestPermission();
    return permission === "default" ? "unknown" : permission;
  }

  const permission = await Notification.requestPermission();
  if (permission !== "granted")
    return permission === "default" ? "unknown" : permission;

  const token = await getAccessToken();
  if (!token) return "granted";
  const reg = await navigator.serviceWorker.register("/sw.js");
  let subscription = await reg.pushManager.getSubscription();
  if (!subscription) {
    const key = await getVapidPublicKey(apiBase, token);
    if (!key) return "granted";
    subscription = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(key).buffer as ArrayBuffer,
    });
  }

  await fetch(
    `${apiBase.replace(/\/$/, "")}/api/notifications/web-push/subscribe`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        categories: {},
        severityThreshold: "low",
        subscription: subscription.toJSON(),
      }),
    },
  ).catch(() => null);
  return "granted";
}

async function getVapidPublicKey(
  apiBase: string,
  token: string,
): Promise<string | null> {
  if (BUILD_VAPID_PUBLIC_KEY) return BUILD_VAPID_PUBLIC_KEY;
  if (runtimeVapidPublicKey) return runtimeVapidPublicKey;
  const res = await fetch(
    `${apiBase.replace(/\/$/, "")}/api/notifications/web-push/public-key`,
    { headers: { Authorization: `Bearer ${token}` } },
  ).catch(() => null);
  if (!res?.ok) return null;
  const data = (await res.json().catch(() => ({}))) as { publicKey?: string };
  runtimeVapidPublicKey = data.publicKey || null;
  return runtimeVapidPublicKey;
}

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const raw = atob(base64.replace(/-/g, "+").replace(/_/g, "/") + padding);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

function toOnboardingStatus(
  state: PermissionState,
): OnboardingPermissionStatus {
  if (state === "granted" || state === "denied") return state;
  return "unknown";
}
